/**
 * Breadcrumb Component
 *
 * UX12: Navigation breadcrumbs for contextual wayfinding in admin pages
 *
 * Features:
 * - Auto-generated breadcrumb paths from route structure
 * - Clickable navigation to parent pages
 * - Current page highlighted and non-clickable
 * - Mobile-responsive (collapses to show only parent + current)
 * - Keyboard accessible with proper ARIA attributes
 * - Separator icons (chevron right)
 *
 * @example
 * ```tsx
 * <Breadcrumb
 *   items={[
 *     { label: 'Admin', href: '/admin' },
 *     { label: 'Projects', href: '/admin/projects' },
 *     { label: 'Edit Project', href: '/admin/projects/123', current: true }
 *   ]}
 * />
 * ```
 */

import { ReactNode } from 'react'

export interface BreadcrumbItem {
  /** Display label for the breadcrumb */
  label: string
  /** Link href (omit for current page) */
  href?: string
  /** Whether this is the current page */
  current?: boolean
  /** Optional icon to display before label */
  icon?: ReactNode
}

export interface BreadcrumbProps {
  /** Array of breadcrumb items (in order from root to current) */
  items: BreadcrumbItem[]
  /** Separator character/icon (default: chevron) */
  separator?: ReactNode
  /** Show all breadcrumbs on mobile (default: false, shows only parent + current) */
  showAllOnMobile?: boolean
}

/**
 * Breadcrumb navigation component
 */
export function Breadcrumb({
  items,
  separator = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M6 4L10 8L6 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  showAllOnMobile = false,
}: BreadcrumbProps) {
  // Filter out items without labels
  const validItems = items.filter((item) => item.label)

  if (validItems.length === 0) {
    return null
  }

  // For mobile: show only parent + current if more than 2 items
  const mobileItems =
    !showAllOnMobile && validItems.length > 2
      ? [validItems[validItems.length - 2], validItems[validItems.length - 1]]
      : validItems

  return (
    <nav aria-label="Breadcrumb" style={{ marginBottom: 'var(--spacing-lg)' }}>
      {/* Desktop view */}
      <ol
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          listStyle: 'none',
          margin: 0,
          padding: 0,
          gap: 'var(--spacing-xs)',
        }}
        className="breadcrumb-desktop"
      >
        {validItems.map((item, index) => {
          const isLast = index === validItems.length - 1
          const isCurrent = item.current || isLast

          return (
            <li
              key={`${item.label}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)',
              }}
            >
              {/* Breadcrumb link or text */}
              {isCurrent || !item.href ? (
                <span
                  aria-current={isCurrent ? 'page' : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-xs)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: isCurrent ? 600 : 400,
                    color: isCurrent
                      ? 'var(--color-text-primary)'
                      : 'var(--color-text-secondary)',
                  }}
                >
                  {item.icon && <span style={{ display: 'flex' }}>{item.icon}</span>}
                  {item.label}
                </span>
              ) : (
                <a
                  href={item.href}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-xs)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 400,
                    color: 'var(--color-text-secondary)',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--color-primary)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--color-text-secondary)'
                  }}
                >
                  {item.icon && <span style={{ display: 'flex' }}>{item.icon}</span>}
                  {item.label}
                </a>
              )}

              {/* Separator (not for last item) */}
              {!isLast && (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--color-text-disabled)',
                    opacity: 0.5,
                  }}
                  aria-hidden="true"
                >
                  {separator}
                </span>
              )}
            </li>
          )
        })}
      </ol>

      {/* Mobile view (collapsed) */}
      {!showAllOnMobile && mobileItems !== validItems && (
        <ol
          style={{
            display: 'none',
            flexWrap: 'wrap',
            alignItems: 'center',
            listStyle: 'none',
            margin: 0,
            padding: 0,
            gap: 'var(--spacing-xs)',
          }}
          className="breadcrumb-mobile"
        >
          {/* Show ellipsis for collapsed items */}
          {mobileItems.length < validItems.length && (
            <li
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-disabled)',
                }}
              >
                ...
              </span>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--color-text-disabled)',
                  opacity: 0.5,
                }}
                aria-hidden="true"
              >
                {separator}
              </span>
            </li>
          )}

          {mobileItems.map((item, index) => {
            const isLast = index === mobileItems.length - 1
            const isCurrent = item.current || isLast

            return (
              <li
                key={`${item.label}-mobile-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xs)',
                }}
              >
                {/* Breadcrumb link or text */}
                {isCurrent || !item.href ? (
                  <span
                    aria-current={isCurrent ? 'page' : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-xs)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: isCurrent ? 600 : 400,
                      color: isCurrent
                        ? 'var(--color-text-primary)'
                        : 'var(--color-text-secondary)',
                    }}
                  >
                    {item.icon && <span style={{ display: 'flex' }}>{item.icon}</span>}
                    {item.label}
                  </span>
                ) : (
                  <a
                    href={item.href}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-xs)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 400,
                      color: 'var(--color-text-secondary)',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {item.icon && <span style={{ display: 'flex' }}>{item.icon}</span>}
                    {item.label}
                  </a>
                )}

                {/* Separator (not for last item) */}
                {!isLast && (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--color-text-disabled)',
                      opacity: 0.5,
                    }}
                    aria-hidden="true"
                  >
                    {separator}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      )}

      {/* CSS for mobile/desktop toggle */}
      <style>{`
        @media (min-width: 640px) {
          .breadcrumb-desktop {
            display: flex !important;
          }
          .breadcrumb-mobile {
            display: none !important;
          }
        }
        @media (max-width: 639px) {
          .breadcrumb-desktop {
            display: ${showAllOnMobile ? 'flex' : 'none'} !important;
          }
          .breadcrumb-mobile {
            display: ${showAllOnMobile ? 'none' : 'flex'} !important;
          }
        }
      `}</style>
    </nav>
  )
}

Breadcrumb.displayName = 'Breadcrumb'
